import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const FloatingFeaturedBadge = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Floating Badge */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 z-50 group"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="relative">
          {/* Neon Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-full blur-xl opacity-75 group-hover:opacity-100 transition-opacity animate-pulse" />
          
          {/* Badge Content */}
          <div className="relative bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white px-5 py-3 rounded-full font-bold text-sm shadow-2xl flex items-center gap-2">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span>Featured</span>
          </div>
        </div>
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 p-8 rounded-t-2xl">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">VeryTrust Featured</h2>
                      <p className="text-white/80 text-sm">Advanced Detection Technology</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      ✨ Premium Features Unlocked
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Advanced tools for comprehensive content verification
                    </p>
                  </div>

                  {/* Feature Cards */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-purple-50 to-cyan-50 dark:from-gray-800 dark:to-gray-900 p-6 rounded-xl border-2 border-purple-200 dark:border-purple-800">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">
                        🔍 Hoax DNA Fingerprinting
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        Track hoax evolution with unique fingerprints. Detect variants across languages and identify similar hoaxes with 95% accuracy.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 p-6 rounded-xl border-2 border-green-200 dark:border-green-800">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">
                        📝 Plagiarism Detection
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        Comprehensive plagiarism checking: web search, document comparison, and AI-generated content detection for writers and researchers.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-800 dark:to-gray-900 p-6 rounded-xl border-2 border-orange-200 dark:border-orange-800">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">
                        ⚡ Real-Time Trending Analysis
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        24-hour auto-refresh of trending hoaxes and hot news from global and local sources, keeping you informed in real-time.
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                      <p>Powered by <span className="font-semibold text-purple-600 dark:text-purple-400">Advanced AI & Pattern Matching</span></p>
                      <p className="mt-1">Built by Taruna Nusantara Team 🇮🇩</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
